import { useEffect, useRef, useState } from 'react';

/**
 * useAutoSave — debounced background save of form state.
 *
 * @param {object} value         The current form values (any shape).
 * @param {(value)=>Promise<any>} saveFn  Async function that PUTs the form.
 * @param {object} opts
 *   - delay:   debounce ms (default 1500)
 *   - skipFirst: skip the very first run (so an initial fetch doesn't trigger
 *     an immediate write back). Default true.
 *   - validate: optional (value)=>boolean — if false, skip the save.
 *
 * Returns: { status, lastSaved, saveNow }
 *   - status: 'idle' | 'saving' | 'saved' | 'error'
 *   - lastSaved: Date | null
 *   - saveNow(): force-flush the pending change immediately (e.g. on blur)
 */
export default function useAutoSave(value, saveFn, opts = {}) {
  const { delay = 1500, skipFirst = true, validate } = opts;
  const [status, setStatus] = useState('idle');
  const [lastSaved, setLastSaved] = useState(null);
  const firstRun = useRef(skipFirst);
  const timerRef = useRef(null);
  const inFlight = useRef(false);
  const pendingValue = useRef(value);
  const saveFnRef = useRef(saveFn);
  saveFnRef.current = saveFn;

  const runSave = async (val) => {
    if (validate && !validate(val)) return;
    if (inFlight.current) {
      pendingValue.current = val;
      return;
    }
    inFlight.current = true;
    setStatus('saving');
    try {
      await saveFnRef.current(val);
      setStatus('saved');
      setLastSaved(new Date());
    } catch {
      setStatus('error');
    } finally {
      inFlight.current = false;
      // If the value moved while we were saving, save again
      if (pendingValue.current !== val) {
        const next = pendingValue.current;
        pendingValue.current = next;
        // small delay to avoid hammering on fast typers
        setTimeout(() => runSave(next), 50);
      }
    }
  };

  useEffect(() => {
    pendingValue.current = value;
    if (firstRun.current) {
      firstRun.current = false;
      return;
    }
    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => runSave(value), delay);
    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value, delay]);

  const saveNow = () => {
    if (timerRef.current) clearTimeout(timerRef.current);
    if (firstRun.current) return;
    return runSave(pendingValue.current);
  };

  return { status, lastSaved, saveNow };
}
